import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { cn } from "lib/utils";

export const tabs = ["needs-coverage", "pending-approval", "direct-offers", "closing-soon", "offers-out", "approved", "canceled"] as const;
export type Tab = typeof tabs[number];
const DEFAULT_TAB: Tab = "needs-coverage";

export const useMenuTab = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const tab = useMemo<Tab>(() => {
        const value = searchParams.get("tab");
        return tabs.includes(value as Tab) ? (value as Tab) : DEFAULT_TAB;
    }, [searchParams]);

    const setTab = useCallback((value: Tab) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", value);
            next.delete("page");
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    return [tab, setTab] as const;
};

type MenuProps = {
    value: Tab;
    onChange: (value: Tab) => void;
    counts?: Partial<Record<Tab, number>>;
};

function Menu({ value, onChange, counts }: MenuProps) {
    const { t } = useTranslation();

    return (
        <div className="tw-inline-flex tw-items-center tw-gap-1 tw-p-1 tw-rounded-lg tw-bg-gray-100">
            {tabs.map((tab) => {
                const isActive = value === tab;
                const count = counts?.[tab];

                return (
                    <button
                        key={tab}
                        type="button"
                        onClick={() => onChange(tab)}
                        className={cn(
                            "tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-transition-colors tw-cursor-pointer",
                            isActive
                                ? "tw-bg-white tw-text-gray-900 tw-shadow-sm"
                                : "tw-text-gray-500 hover:tw-text-gray-900"
                        )}
                    >
                        {t(tab)}
                        {count !== undefined && (
                            <span className="tw-min-w-5 tw-px-1.5 tw-rounded tw-bg-gray-200 tw-text-xs tw-font-medium tw-text-gray-700">
                                {count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

export default Menu;
